import type { AriaAttributes, MouseEvent, MouseEventHandler, ReactElement } from 'react';
import React, { cloneElement, useCallback } from 'react';

import { isSafari } from '../../../utils/devices/device';
import { Size, Variant } from './types';
import { Background, StyledIcon, Wrapper } from './RoundedButton.styled';

interface RoundedButtonProps extends Pick<AriaAttributes, 'aria-label'> {
  /**
   * Icon to display on the button
   */
  Icon: ReactElement;
  /**
   * Changes the size. It can be one of the following options: `RoundedButton.Size.SMALL`, `RoundedButton.Size.BIG`
   */
  size?: Size;
  /**
   * Changes the styles. It can be one of the following options: `RoundedButton.Variant.NEUTRAL`, `RoundedButton.Size.PRIMARY`
   */
  variant?: Variant;
  /**
   * Property used for testing purposes
   */
  dataCy?: string;
  /**
   * Indicates whether the button has been hovered over or not
   */
  isHovered?: boolean;
  /**
   * Tells whether the button is disabled or not
   */
  disabled?: boolean;
  /**
   * Function that is executed when the button is clicked on
   */
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  /**
   * Function that is executed when the mouse button is down over the button.
   */
  onMouseDown?: MouseEventHandler<HTMLButtonElement>;
  /**
   * Function that is executed when the mouse button is down over the button.
   */
  onMouseUp?: MouseEventHandler<HTMLButtonElement>;
  /**
   * Sets the tab-index of the component
   */
  tabIndex?: number;
  /**
   * Makes the button disabled and removes the shadow
   */
  disabledWithoutShadow?: boolean;
}

type ClonedIconProps = (Partial<unknown> & React.Attributes) | undefined;

const RoundedButton = ({
  Icon,
  size = Size.BIG,
  variant = Variant.NEUTRAL,
  dataCy,
  isHovered = false,
  disabled = false,
  disabledWithoutShadow = false,
  onClick,
  onMouseDown,
  onMouseUp,
  tabIndex,
  ...rest
}: RoundedButtonProps) => {
  const ClonedIcon = useCallback(
    (props: ClonedIconProps) => (Icon ? cloneElement(Icon, props) : null),
    [Icon],
  );

  return (
    <Wrapper
      data-cy={dataCy}
      $disabled={disabledWithoutShadow || disabled}
      tabIndex={isSafari ? undefined : tabIndex}
      $size={size}
      $variant={variant}
      $isHovered={isHovered}
      onClick={onClick}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      aria-label={rest['aria-label']}
    >
      <Background
        $size={size}
        $variant={variant}
        $disabled={disabledWithoutShadow || disabled}
        $disabledWithoutShadow={disabledWithoutShadow}
      />
      {!!ClonedIcon && (
        <StyledIcon
          as={ClonedIcon}
          $variant={variant}
          $disabled={disabledWithoutShadow || disabled}
        />
      )}
    </Wrapper>
  );
};

RoundedButton.Size = Size;
RoundedButton.Variant = Variant;

export default RoundedButton;
